import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import authService from "../services/authService";

export default function SignUp() {
  const emailFieldRef = useRef(null);
  const verificationSectionRef = useRef(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    password: "",
    confirmPassword: "",
  });

  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
    mobile: false,
    password: false,
    confirmPassword: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Verification states
  const [showVerification, setShowVerification] = useState(false);
  const [emailOTP, setEmailOTP] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailOTPSent, setEmailOTPSent] = useState(false);
  const [emailVerifying, setEmailVerifying] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [needsEmailVerification, setNeedsEmailVerification] = useState(true);
  const [checkingUser, setCheckingUser] = useState(false);

  // Validation states and messages
  const [errors, setErrors] = useState({
    email: "",
    confirmPassword: "",
    mobile: "",
  });

  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    uppercase: false,
    number: false,
    specialChar: false,
  });

  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const navigate = useNavigate();

  // Regex patterns
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const uppercaseRegex = /[A-Z]/;
  const numberRegex = /[0-9]/;
  const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;

  // Phone validation: exactly 10 digits, no special characters or letters, not all same digit
  const validatePhone = (phone) => {
    const trimmed = phone.trim();
    // Must be exactly 10 digits
    if (trimmed.length !== 10) return false;
    // Must contain only digits
    if (!/^\d+$/.test(trimmed)) return false;
    // Must not be all the same digit (like 0000000000, 1111111111, etc.)
    if (new Set(trimmed).size === 1) return false;
    return true;
  };

  // Validate email on blur or change after touched
  useEffect(() => {
    if (touched.email) {
      if (emailRegex.test(formData.email.trim())) {
        setErrors((prev) => ({ ...prev, email: "" }));
      } else {
        setErrors((prev) => ({ ...prev, email: "Please enter a valid email address." }));
      }
    }
  }, [formData.email, touched.email]);

  // Validate mobile (required) on blur or change after touched
  useEffect(() => {
    if (touched.mobile) {
      if (validatePhone(formData.mobile)) {
        setErrors((prev) => ({ ...prev, mobile: "" }));
      } else {
        const trimmed = formData.mobile.trim();
        if (trimmed.length === 0) {
          setErrors((prev) => ({ ...prev, mobile: "Mobile number is required" }));
        } else if (trimmed.length !== 10) {
          setErrors((prev) => ({ ...prev, mobile: "Mobile number must be exactly 10 digits" }));
        } else if (!/^\d+$/.test(trimmed)) {
          setErrors((prev) => ({ ...prev, mobile: "Mobile number must contain only digits (0-9)" }));
        } else if (new Set(trimmed).size === 1) {
          setErrors((prev) => ({ ...prev, mobile: "Please enter a valid mobile number" }));
        } else {
          setErrors((prev) => ({ ...prev, mobile: "Please enter a valid 10-digit mobile number" }));
        }
      }
    }
  }, [formData.mobile, touched.mobile]);

  // Reset email verification if email changes
  useEffect(() => {
    if (emailVerified && formData.email) {
      setEmailVerified(false);
      setEmailOTPSent(false);
      setEmailOTP("");
      setEmailError("");
    }
  }, [formData.email]);

  // Password live validation
  useEffect(() => {
    const pass = formData.password;
    setPasswordChecks({
      length: pass.length >= 6,
      uppercase: uppercaseRegex.test(pass),
      number: numberRegex.test(pass),
      specialChar: specialCharRegex.test(pass),
    });

    // Validate confirm password on password or confirm password change if touched
    if (touched.confirmPassword) {
      if (formData.confirmPassword === pass) {
        setErrors((prev) => ({ ...prev, confirmPassword: "" }));
      } else {
        setErrors((prev) => ({ ...prev, confirmPassword: "Passwords do not match." }));
      }
    }
  }, [formData.password, formData.confirmPassword, touched.confirmPassword]);

  // Confirm password validation on blur
  useEffect(() => {
    if (touched.confirmPassword) {
      if (formData.confirmPassword === formData.password) {
        setErrors((prev) => ({ ...prev, confirmPassword: "" }));
      } else {
        setErrors((prev) => ({ ...prev, confirmPassword: "Passwords do not match." }));
      }
    }
  }, [formData.confirmPassword, formData.password, touched.confirmPassword]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    // For mobile field, only allow digits
    if (name === 'mobile') {
      // Remove any non-digit characters
      const digitsOnly = value.replace(/\D/g, '');
      // Limit to 10 digits
      const limitedValue = digitsOnly.slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: limitedValue }));
      // Clear "already registered" error when user changes mobile number
      if (errors.mobile && errors.mobile.includes("already registered")) {
        setErrors((prev) => ({ ...prev, mobile: "" }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Handle blur (field touched)
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  // Toggle password visibility
  const toggleShowPassword = () => setShowPassword((v) => !v);
  const toggleShowConfirmPassword = () => setShowConfirmPassword((v) => !v);


  // Send Email OTP
  const handleSendEmailOTP = async () => {
    if (!emailRegex.test(formData.email.trim())) {
      setTouched((prev) => ({ ...prev, email: true }));
      setEmailError("Please enter a valid email address");
      scrollToEmailField();
      return;
    }

    setEmailError("");
    setEmailSending(true);

    try {
      // Check if email exists in database first
      const checkResponse = await authService.checkUserExists(
        formData.email.trim(),
        formData.mobile.trim()  // Mobile is mandatory but not verified
      );

      if (checkResponse.success && checkResponse.email_exists) {
        setEmailError("This email is already registered. Please use a different email or login.");
        setEmailSending(false);
        scrollToEmailField();
        return;
      }

      // Check if mobile number exists
      if (checkResponse.success && checkResponse.phone_exists) {
        setErrors((prev) => ({ ...prev, mobile: "This mobile number is already registered. Please use a different number or login." }));
        setTouched((prev) => ({ ...prev, mobile: true }));
        setEmailSending(false);
        scrollToEmailField();
        return;
      }

      // Email doesn't exist, send OTP via AuthKey
      // Pass firstName for personalization in email
      const response = await authService.sendEmailOTP(
        formData.email.trim(),
        formData.firstName.trim() || null
      );
      if (response.success) {
        setEmailOTPSent(true);
        setEmailError("");
      } else {
        setEmailError(response.error || "Failed to send OTP. Please try again.");
        scrollToEmailField();
      }
    } catch (error) {
      setEmailError(error.message || "Failed to send OTP. Please try again.");
      scrollToEmailField();
    } finally {
      setEmailSending(false);
    }
  };

  // Mobile OTP functions - COMMENTED OUT (not used, kept for future reference)
  // Mobile verification will be implemented later
  /*
  // Send Mobile OTP
  const handleSendMobileOTP = async () => {
    if (!validatePhone(formData.mobile)) {
      setTouched((prev) => ({ ...prev, mobile: true }));
      setMobileError("Please enter a valid 10-digit mobile number");
      return;
    }

    setMobileError("");
    setMobileSending(true);

    try {
      // Check if mobile exists in database first
      const checkResponse = await authService.checkUserExists(
        formData.email.trim(),
        formData.mobile.trim()
      );

      if (checkResponse.success && checkResponse.phone_exists) {
        setMobileError("This mobile number is already registered. Please use a different number or login.");
        setMobileSending(false);
        return;
      }

      // Mobile doesn't exist, send OTP via backend (which can use AuthKey for WhatsApp)
      const response = await authService.sendMobileOTP(formData.mobile.trim());
      if (response.success) {
        setMobileOTPSent(true);
        setMobileError("");
      } else {
        setMobileError(response.error || "Failed to send OTP. Please try again.");
      }
    } catch (error) {
      setMobileError(error.message || "Failed to send OTP. Please try again.");
    } finally {
      setMobileSending(false);
    }
  };

  // Verify Mobile OTP
  const handleVerifyMobileOTP = async () => {
    if (!mobileOTP || mobileOTP.length !== 6) {
      setMobileError("Please enter a valid 6-digit OTP");
      return;
    }

    setMobileError("");
    setMobileVerifying(true);

    try {
      const response = await authService.verifyMobileOTP(formData.mobile.trim(), mobileOTP);
      if (response.success) {
        setMobileVerified(true);
        setMobileError("");
      } else {
        setMobileError(response.error || "Invalid OTP. Please try again.");
      }
    } catch (error) {
      setMobileError(error.message || "Invalid OTP. Please try again.");
    } finally {
      setMobileVerifying(false);
    }
  };
  */

  // Verify Email OTP
  const handleVerifyEmailOTP = async () => {
    if (!emailOTP || emailOTP.length !== 6) {
      setEmailError("Please enter a valid 6-digit OTP");
      return;
    }

    setEmailError("");
    setEmailVerifying(true);

    try {
      const response = await authService.verifyEmailOTP(formData.email.trim(), emailOTP);
      if (response.success) {
        setEmailVerified(true);
        setEmailError("");
      } else {
        setEmailError(response.error || "Invalid OTP. Please try again.");
      }
    } catch (error) {
      setEmailError(error.message || "Invalid OTP. Please try again.");
    } finally {
      setEmailVerifying(false);
    }
  };

  // Field validation checks
  const isFirstNameValid = formData.firstName.trim().length >= 2;
  const isLastNameValid = formData.lastName.trim().length >= 2;
  const isEmailValid = emailRegex.test(formData.email.trim());
  const isMobileValid = validatePhone(formData.mobile); // Mobile is mandatory
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);
  const isConfirmPasswordValid = formData.confirmPassword === formData.password && formData.confirmPassword.length > 0;

  // Form valid only if required fields valid, passwords match, and SMS consent is checked
  const isFormValid = isFirstNameValid && isLastNameValid && isEmailValid && isMobileValid && isPasswordValid && isConfirmPasswordValid && smsConsent;

  // Scroll to email field or verification section
  const scrollToEmailField = () => {
    setTimeout(() => {
      const targetElement = verificationSectionRef.current || emailFieldRef.current;
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }, 100);
  };

  // Form submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid) {
      setTouched({ firstName: true, lastName: true, email: true, mobile: true, password: true, confirmPassword: true });
      setFormError("Please fix validation errors.");
      // Scroll to email field to show validation errors
      scrollToEmailField();
      return;
    }

    setFormError("");
    setCheckingUser(true);

    try {
      // Check if user exists
      const checkResponse = await authService.checkUserExists(
        formData.email.trim(),
        formData.mobile.trim()  // Mobile is mandatory but not verified
      );

      if (checkResponse.success) {
        const { email_exists, phone_exists } = checkResponse;

        // If email exists, user is already registered (email is mandatory)
        if (email_exists) {
          setFormError("This email is already registered. Please login instead.");
          setCheckingUser(false);
          // Scroll to show error message
          scrollToEmailField();
          return;
        }

        // If mobile number exists, user is already registered
        if (phone_exists) {
          setErrors((prev) => ({ ...prev, mobile: "This mobile number is already registered. Please use a different number or login." }));
          setTouched((prev) => ({ ...prev, mobile: true }));
          setCheckingUser(false);
          // Scroll to show error message
          scrollToEmailField();
          return;
        }

        // Email verification is mandatory
        setNeedsEmailVerification(true);

        // Show verification prompt
        setShowVerification(true);
        setCheckingUser(false);

        // Auto-send email OTP (mandatory)
        if (!email_exists) {
          await handleSendEmailOTP();
        } else {
          setEmailVerified(true);
        }

        // Scroll to verification section after showing it
        scrollToEmailField();
      } else {
        setFormError(checkResponse.error || "Failed to check user. Please try again.");
        setCheckingUser(false);
        // Scroll to show error message
        scrollToEmailField();
      }
    } catch (error) {
      console.error('Check user error:', error);
      setFormError(error.message || "An error occurred. Please try again.");
      setCheckingUser(false);
      // Scroll to show error message
      scrollToEmailField();
    }
  };

  // Complete registration after verification
  const handleCompleteRegistration = async () => {
    // Email verification is mandatory
    if (!emailVerified) {
      setFormError("Please verify your email address before completing registration.");
      return;
    }

    setIsSubmitting(true);
    setFormError("");

    try {
      const userData = {
        email: formData.email.trim(),
        password: formData.password,
        password_confirm: formData.confirmPassword,
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        phone: formData.mobile.trim(),
      };

      const response = await authService.userRegister(userData);

      if (response.success) {
        navigate("/user/dashboard");
      } else {
        setFormError(response.error || "Registration failed");
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Registration error:', error);
      setFormError(error.message || "An error occurred during registration");
      setIsSubmitting(false);
    }
  };

  // Small reusable component to show password criteria
  function PasswordLine({ isValid, text }) {
    return (
      <li
        className="d-flex align-items-center"
        style={{ color: isValid ? "#28a745" : "#dc3545", userSelect: "none" }}
      >
        <i className={`bi me-2 ${isValid ? "bi-check-circle-fill" : "bi-x-circle-fill"}`}></i> {text}
      </li>
    );
  }

  return (
    <div className="container py-5" style={{ maxWidth: 500 }}>
      <h2
        className="fw-bold mb-4 text-center"
        style={{ fontFamily: "'Poppins', sans-serif", color: "#6B46C1" }}
      >
        Create Your Account
      </h2>

      <form noValidate onSubmit={handleSubmit}>
        {/* Global error */}
        {formError && (
          <div className="alert alert-danger" role="alert">
            {formError}
          </div>
        )}

        {/* Mobile Number Field - Mandatory (no OTP verification) */}
        <div className="mb-3">
          <label htmlFor="mobile" className="form-label fw-semibold" style={{ color: "#6B46C1" }}>
            Mobile Number <sup className="text-danger">*</sup>
          </label>
          <input
            type="tel"
            id="mobile"
            name="mobile"
            className={`form-control ${(touched.mobile && (!isMobileValid || errors.mobile))
                ? "is-invalid"
                : touched.mobile && isMobileValid && !errors.mobile
                  ? "is-valid"
                  : ""
              }`}
            placeholder="Enter 10-digit mobile number"
            value={formData.mobile}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isSubmitting || checkingUser}
            required
            maxLength={10}
            pattern="[0-9]{10}"
            autoComplete="tel"
          />
          {touched.mobile && (errors.mobile || !isMobileValid) && (
            <div className="invalid-feedback d-block">
              {errors.mobile || (formData.mobile.trim().length === 0
                ? "Mobile number is required"
                : formData.mobile.trim().length !== 10
                  ? "Mobile number must be exactly 10 digits"
                  : !/^\d+$/.test(formData.mobile.trim())
                    ? "Mobile number must contain only digits (0-9)"
                    : new Set(formData.mobile.trim()).size === 1
                      ? "Please enter a valid mobile number"
                      : "Please enter a valid 10-digit mobile number")}
            </div>
          )}
        </div>

        {/* Email Field */}
        <div className="mb-3" ref={emailFieldRef}>
          <label htmlFor="email" className="form-label fw-semibold" style={{ color: "#6B46C1" }}>
            Email Address <sup className="text-danger">*</sup>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            className={`form-control ${touched.email && errors.email ? "is-invalid" : ""}`}
            placeholder="you@example.com"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isSubmitting || checkingUser}
            required
            autoComplete="email"
          />
          {touched.email && errors.email && (
            <div className="invalid-feedback d-block">{errors.email}</div>
          )}
        </div>

        {/* Verification Section - Only show after clicking signup */}
        {showVerification && (
          <div className="mb-4 p-3 border rounded bg-light" ref={verificationSectionRef}>
            <h6 className="mb-3 fw-semibold" style={{ color: "#6B46C1" }}>
              <i className="bi bi-shield-check me-2"></i>
              Verify Your Contact Information
            </h6>
            <p className="text-muted small mb-3">
              Please verify your email address to complete registration.
            </p>

            {/* Email Verification - Mandatory */}
            {needsEmailVerification && (
              <div className="mb-3">
                {emailVerified ? (
                  <div className="text-success mb-2">
                    <i className="bi bi-check-circle me-1"></i>
                    Email verified
                  </div>
                ) : (
                  <>
                    <label className="form-label small fw-semibold">
                      Email Verification
                    </label>
                    {!emailOTPSent ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary w-100"
                        onClick={handleSendEmailOTP}
                        disabled={emailSending}
                      >
                        {emailSending ? "Sending OTP..." : "Send OTP to Email"}
                      </button>
                    ) : (
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Enter 6-digit OTP"
                          value={emailOTP}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setEmailOTP(value);
                            setEmailError("");
                          }}
                          maxLength={6}
                          autoComplete="one-time-code"
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-success"
                          onClick={handleVerifyEmailOTP}
                          disabled={emailOTP.length !== 6 || emailVerifying}
                        >
                          {emailVerifying ? "Verifying..." : "Verify"}
                        </button>
                      </div>
                    )}
                    {emailError && (
                      <div className="text-danger small mt-1">{emailError}</div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Complete Registration Button */}
            {emailVerified && (
              <button
                type="button"
                className="btn btn-success w-100 mt-3"
                onClick={handleCompleteRegistration}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Completing Registration..." : "Complete Registration"}
              </button>
            )}
          </div>
        )}

        {/* First Name */}
        <div className="mb-3">
          <label htmlFor="firstName" className="form-label fw-semibold" style={{ color: "#6B46C1" }}>
            First Name <sup className="text-danger">*</sup>
          </label>
          <input
            id="firstName"
            name="firstName"
            type="text"
            className={`form-control ${touched.firstName ? (isFirstNameValid ? "is-valid" : "is-invalid") : ""}`}
            placeholder="Your first name"
            value={formData.firstName}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            minLength={2}
            autoComplete="given-name"
          />
          {touched.firstName && !isFirstNameValid && (
            <div className="invalid-feedback">Please enter your first name (at least 2 characters).</div>
          )}
        </div>

        {/* Last Name */}
        <div className="mb-3">
          <label htmlFor="lastName" className="form-label fw-semibold" style={{ color: "#6B46C1" }}>
            Last Name <sup className="text-danger">*</sup>
          </label>
          <input
            id="lastName"
            name="lastName"
            type="text"
            className={`form-control ${touched.lastName ? (isLastNameValid ? "is-valid" : "is-invalid") : ""}`}
            placeholder="Your last name"
            value={formData.lastName}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            minLength={2}
            autoComplete="family-name"
          />
          {touched.lastName && !isLastNameValid && (
            <div className="invalid-feedback">Please enter your last name (at least 2 characters).</div>
          )}
        </div>

        {/* Password */}
        <div className="mb-3 position-relative">
          <label htmlFor="password" className="form-label fw-semibold" style={{ color: "#6B46C1" }}>
            Password <sup className="text-danger">*</sup>
          </label>
          <input
            type={showPassword ? "text" : "password"}
            id="password"
            name="password"
            className={`form-control ${touched.password && !isPasswordValid ? "is-invalid" : ""}`}
            placeholder="Enter your password"
            value={formData.password}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={toggleShowPassword}
            className="btn btn-outline-secondary btn-sm position-absolute top-50 end-0 translate-middle-y me-2"
            style={{ zIndex: 5, borderRadius: "0 0.375rem 0.375rem 0", marginTop: '15px' }}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
              <i className="bi bi-eye-slash-fill"></i>
            ) : (
              <i className="bi bi-eye-fill"></i>
            )}
          </button>
          {touched.password && !isPasswordValid && (
            <div className="invalid-feedback d-block">
              Password does not meet all criteria below.
            </div>
          )}
        </div>

        {/* Password criteria box */}
        <div className="mb-4 p-3 border rounded bg-light" style={{ fontSize: 14, userSelect: "none" }}>
          <ul className="mb-0 ps-3">
            <PasswordLine
              isValid={passwordChecks.length}
              text="Minimum 6 characters"
            />
            <PasswordLine
              isValid={passwordChecks.uppercase}
              text="At least 1 uppercase letter"
            />
            <PasswordLine
              isValid={passwordChecks.number}
              text="At least 1 number"
            />
            <PasswordLine
              isValid={passwordChecks.specialChar}
              text="At least 1 special character (!@#$%^&*)"
            />
          </ul>
        </div>

        {/* Confirm Password */}
        <div className="mb-4 position-relative">
          <label htmlFor="confirmPassword" className="form-label fw-semibold" style={{ color: "#6B46C1" }}>
            Confirm Password <sup className="text-danger">*</sup>
          </label>
          <input
            type={showConfirmPassword ? "text" : "password"}
            id="confirmPassword"
            name="confirmPassword"
            className={`form-control ${touched.confirmPassword && errors.confirmPassword ? "is-invalid" : ""}`}
            placeholder="Re-enter your password"
            value={formData.confirmPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={toggleShowConfirmPassword}
            className="btn btn-outline-secondary btn-sm position-absolute top-50 end-0 translate-middle-y me-2"
            style={{ zIndex: 5, borderRadius: "0 0.375rem 0.375rem 0", marginTop: '15px' }}
            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
          >
            {showConfirmPassword ? (
              <i className="bi bi-eye-slash-fill"></i>
            ) : (
              <i className="bi bi-eye-fill"></i>
            )}
          </button>
          {touched.confirmPassword && errors.confirmPassword && (
            <div className="invalid-feedback d-block">{errors.confirmPassword}</div>
          )}
        </div>

        {/* SMS Consent Checkbox */}
        <div className="mb-4">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="smsConsent"
              checked={smsConsent}
              onChange={(e) => setSmsConsent(e.target.checked)}
              disabled={isSubmitting || checkingUser}
              style={{ marginTop: "0.25rem" }}
            />
            <label className="form-check-label" htmlFor="smsConsent" style={{ fontSize: "0.9rem", lineHeight: "1.5", color: "#2f2f31ff" }}>
              By subscribing and submitting, you agree to receive notifications from Nexen Consults LLC through calls and SMS. Data rates apply and messaging frequency may vary. You can unsubscribe at any time. We respect your privacy and won’t share your information with third parties.
            </label>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="btn btn-primary w-100 fw-semibold"
          disabled={!isFormValid || isSubmitting || checkingUser || showVerification}
          aria-disabled={!isFormValid || isSubmitting || checkingUser || showVerification}
          style={{
            padding: "12px",
            fontSize: "1rem"
          }}
        >
          {checkingUser ? "Checking..." : isSubmitting ? "Signing Up..." : "Sign Up"}
        </button>
      </form>
    </div>
  );
}

function PasswordLine({ isValid, text }) {
  return (
    <li
      style={{ color: isValid ? "#28a745" : "#dc3545", userSelect: "none" }}
      className="d-flex align-items-center"
    >
      <i className={`bi me-2 ${isValid ? "bi-check-circle-fill" : "bi-x-circle-fill"}`}></i>
      {text}
    </li>
  );
}